import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { getWorkspaceRoomId } from "../utils/callRoom";

const defaultParticipantState = {
  joined: false,
  online: false,
  inCall: false,
  video: false,
  audio: false,
  screenSharing: false,
  connectionState: "disconnected",
  lastSeen: null,
};

const callDocRef = (problemId) => doc(db, "calls", getWorkspaceRoomId(problemId));

export const ensureCallPresenceDoc = async (problemId, participantIds = []) => {
  if (!problemId) return;
  const participantsSeed = participantIds.reduce((acc, uid) => {
    if (!uid) return acc;
    acc[uid] = { ...defaultParticipantState };
    return acc;
  }, {});

  await setDoc(
    callDocRef(problemId),
    {
      roomId: getWorkspaceRoomId(problemId),
      problemId,
      active: false,
      callActive: false,
      updatedAt: serverTimestamp(),
      participants: participantsSeed,
    },
    { merge: true }
  );
};

export const startCallPresence = async (
  problemId,
  callerId,
  participantIds = []
) => {
  if (!problemId || !callerId) {
    throw new Error("problemId and callerId are required");
  }

  const ref = callDocRef(problemId);
  const participantsSeed = participantIds.reduce((acc, uid) => {
    if (!uid) return acc;
    acc[uid] = { ...defaultParticipantState };
    return acc;
  }, {});

  await setDoc(
    ref,
    {
      roomId: getWorkspaceRoomId(problemId),
      problemId,
      active: true,
      callActive: true,
      callerId,
      startedAt: serverTimestamp(),
      endedAt: null,
      updatedAt: serverTimestamp(),
      participants: participantsSeed,
    },
    { merge: true }
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Ringing lifecycle (phone-style call invitations)
//
// The shared calls/{roomId} document carries a `status` field that drives the
// ringing UX on both sides:
//   "ringing"  → caller initiated, waiting for the callee to answer
//   "active"   → callee accepted; both sides mount the live WebRTC call
//   "declined" → callee rejected the call
//   "missed"   → callee did not answer within the timeout window
//   "ended" / "idle" → no live call in progress
// ─────────────────────────────────────────────────────────────────────────────

export const startRingingCall = async (
  problemId,
  callerId,
  participantIds = []
) => {
  if (!problemId || !callerId) {
    throw new Error("problemId and callerId are required");
  }

  const ref = callDocRef(problemId);
  const participantsSeed = participantIds.reduce((acc, uid) => {
    if (!uid) return acc;
    acc[uid] = { ...defaultParticipantState };
    return acc;
  }, {});

  await setDoc(
    ref,
    {
      roomId: getWorkspaceRoomId(problemId),
      problemId,
      active: true,
      callActive: true,
      status: "ringing",
      initiatedBy: callerId,
      initiatedAt: serverTimestamp(),
      // callerId is what WebRTCCall reads to decide who creates the offer.
      callerId,
      startedAt: serverTimestamp(),
      endedAt: null,
      acceptedBy: null,
      declinedBy: null,
      // Clear stale SDP from any previous call so the new negotiation is clean.
      offer: null,
      answer: null,
      updatedAt: serverTimestamp(),
      participants: participantsSeed,
    },
    { merge: true }
  );
};

export const acceptCall = async (problemId, uid) => {
  if (!problemId || !uid) {
    throw new Error("problemId and uid are required");
  }
  await setDoc(
    callDocRef(problemId),
    {
      status: "active",
      callActive: true,
      acceptedBy: uid,
      acceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

export const declineCall = async (problemId, uid) => {
  if (!problemId || !uid) {
    throw new Error("problemId and uid are required");
  }
  await setDoc(
    callDocRef(problemId),
    {
      status: "declined",
      callActive: false,
      declinedBy: uid,
      declinedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

/**
 * Flips a still-ringing call to "missed" and logs a persistent system message
 * to the workspace chat. Guarded by a read so we never overwrite a call the
 * callee accepted at the last second (status would already be "active").
 */
export const markCallMissedIfRinging = async (problemId, { callerName } = {}) => {
  if (!problemId) return { missed: false };
  const ref = callDocRef(problemId);

  const snap = await getDoc(ref);
  if (!snap.exists()) return { missed: false };
  const data = snap.data();
  if (data.status !== "ringing") return { missed: false };

  await setDoc(
    ref,
    {
      status: "missed",
      callActive: false,
      missedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  try {
    await addDoc(collection(db, "problems", problemId, "messages"), {
      type: "system",
      text: `📞 Missed call${callerName ? ` from ${callerName}` : ""} — no answer.`,
      createdAt: serverTimestamp(),
      senderId: data.initiatedBy || null,
    });
  } catch (err) {
    console.warn("Could not log missed-call system message", err);
  }

  return { missed: true };
};

/**
 * Resets the call back to an idle state (used when the caller cancels a ring
 * or dismisses a declined/missed result) so the next call starts fresh.
 */
export const resetCallStatus = async (problemId) => {
  if (!problemId) return;
  await setDoc(
    callDocRef(problemId),
    {
      status: "idle",
      active: false,
      callActive: false,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

export const joinCallPresence = async (
  problemId,
  uid,
  mediaState = {
    video: true,
    audio: true,
    screenSharing: false,
    connectionState: "connecting",
  }
) => {
  if (!problemId || !uid) {
    throw new Error("problemId and uid are required");
  }

  const ref = callDocRef(problemId);
  const participantKey = `participants.${uid}`;

  await setDoc(
    ref,
    {
      active: true,
      callActive: true,
      updatedAt: serverTimestamp(),
      [participantKey]: {
        joined: true,
        online: true,
        inCall: true,
        video: Boolean(mediaState.video),
        audio: Boolean(mediaState.audio),
        screenSharing: Boolean(mediaState.screenSharing),
        connectionState: mediaState.connectionState || "connecting",
        joinedAt: serverTimestamp(),
        lastSeen: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
    },
    { merge: true }
  );
};

export const leaveCallPresence = async (problemId, uid) => {
  if (!problemId || !uid) {
    throw new Error("problemId and uid are required");
  }

  await setDoc(
    callDocRef(problemId),
    {
      roomId: getWorkspaceRoomId(problemId),
      problemId,
      updatedAt: serverTimestamp(),
      [`participants.${uid}.joined`]: false,
      [`participants.${uid}.inCall`]: false,
      [`participants.${uid}.video`]: false,
      [`participants.${uid}.audio`]: false,
      [`participants.${uid}.screenSharing`]: false,
      [`participants.${uid}.connectionState`]: "disconnected",
      [`participants.${uid}.online`]: false,
      [`participants.${uid}.lastSeen`]: serverTimestamp(),
      [`participants.${uid}.leftAt`]: serverTimestamp(),
      [`participants.${uid}.updatedAt`]: serverTimestamp(),
    },
    { merge: true }
  );
};

export const updateParticipantMediaState = async (problemId, uid, patch) => {
  if (!problemId || !uid || !patch || typeof patch !== "object") return;

  const updates = {};
  const allowedKeys = [
    "video",
    "audio",
    "screenSharing",
    "joined",
    "inCall",
    "online",
    "connectionState",
  ];

  allowedKeys.forEach((key) => {
    if (key in patch) {
      if (key === "connectionState") {
        updates[`participants.${uid}.${key}`] = String(patch[key] || "connecting");
      } else {
        updates[`participants.${uid}.${key}`] = Boolean(patch[key]);
      }
    }
  });

  updates[`participants.${uid}.updatedAt`] = serverTimestamp();
  updates[`participants.${uid}.lastSeen`] = serverTimestamp();
  updates.updatedAt = serverTimestamp();

  await updateDoc(callDocRef(problemId), updates);
};

export const endCallPresence = async (problemId, endedBy) => {
  if (!problemId) return;

  await setDoc(callDocRef(problemId), {
    roomId: getWorkspaceRoomId(problemId),
    problemId,
    active: false,
    callActive: false,
    status: "ended",
    endedBy: endedBy || null,
    endedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const subscribeToCallPresence = (problemId, callback) => {
  if (!problemId) return () => {};
  return onSnapshot(
    callDocRef(problemId),
    (snap) => {
      callback(
        snap.exists()
          ? {
              id: snap.id,
              ...snap.data(),
            }
          : null
      );
    },
    (error) => {
      console.warn("Call presence listener error:", error);
      callback(null);
    }
  );
};

export const heartbeatParticipantPresence = async (problemId, uid) => {
  if (!problemId || !uid) return;
  const ref = callDocRef(problemId);
  await setDoc(
    ref,
    {
      roomId: getWorkspaceRoomId(problemId),
      problemId,
      updatedAt: serverTimestamp(),
      [`participants.${uid}.online`]: true,
      [`participants.${uid}.lastSeen`]: serverTimestamp(),
      [`participants.${uid}.updatedAt`]: serverTimestamp(),
    },
    { merge: true }
  );
};

export const setParticipantOnlineStatus = async (problemId, uid, online) => {
  if (!problemId || !uid) return;
  await setDoc(
    callDocRef(problemId),
    {
      roomId: getWorkspaceRoomId(problemId),
      problemId,
      updatedAt: serverTimestamp(),
      [`participants.${uid}.online`]: Boolean(online),
      [`participants.${uid}.lastSeen`]: serverTimestamp(),
      [`participants.${uid}.updatedAt`]: serverTimestamp(),
      ...(online
        ? {}
        : {
            [`participants.${uid}.inCall`]: false,
            [`participants.${uid}.joined`]: false,
            [`participants.${uid}.connectionState`]: "disconnected",
          }),
    },
    { merge: true }
  );
};
